<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <button class="close-btn" @click="$emit('close')">×</button>
      <div class="album-info">
        <img
          :src="album.cover_url"
          alt="Album cover">
        <div class="text-and-logo">
          <div class="text-info">
            <p class="artist">{{ album.artist }}</p>
            <p class="title">
              {{ album.title }}
              <span v-if="album.is_test_album" class="test-album-indicator" title="Test Album">
                🧪 Test
              </span>
            </p>
            <div class="tier-badge-header">
              <EnhancedTierBadge
                :tier="album.tier"
                variant="medal"
                size="small"
                :glow-effect="true"
              />
            </div>
          </div>
          <div class="streaming-logos">
            <a v-if="album.spotify_id" :href="`https://open.spotify.com/album/${album.spotify_id}`" target="_blank" rel="noopener noreferrer" class="streaming-logo">
              <img :src="spotifyIcon" alt="Listen on Spotify" class="streaming-icon" />
              <span class="streaming-text">Spotify</span>
            </a>
            <a v-if="album.tidal_album_id" :href="`https://tidal.com/browse/album/${album.tidal_album_id}`" target="_blank" rel="noopener noreferrer" class="streaming-logo">
              <img :src="tidalIcon" alt="Listen on Tidal" class="streaming-icon" />
              <span class="streaming-text">TIDAL</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Single Scrollable Content -->
      <div class="scroll-content">
        <!-- Section 1: Submitted by & Session -->
        <div class="content-section album-details">
          <div class="detail-row">
            <span class="label">Submitted by:</span>
            <span class="value">{{ album.submitted_by }}</span>
          </div>
          <div v-if="albumData && albumData.session" class="detail-row session-context">
            <span class="label">Session:</span>
            <div class="value session-info">
              <div class="session-date">{{ formatSessionDate(albumData.session.session_date) }}</div>
              <div v-if="albumData.session.location" class="session-location">{{ albumData.session.location }}</div>
            </div>
          </div>
          <!-- Main Genres -->
          <div v-if="albumData && albumData.mainGenres && albumData.mainGenres.length > 0" class="detail-row">
            <span class="label">Genre:</span>
            <span class="value genres-value">
              <span v-for="slug in albumData.mainGenres" :key="slug" class="genre-pill genre-pill--main">
                {{ genreLabel(slug, 'main') }}
              </span>
            </span>
          </div>
          <!-- Subgenres — only shown in detail modal, not in filters -->
          <div v-if="albumData && albumData.subGenres && albumData.subGenres.length > 0" class="detail-row">
            <span class="label">Subgenre:</span>
            <span class="value genres-value">
              <span v-for="slug in albumData.subGenres" :key="slug" class="genre-pill genre-pill--sub">
                {{ genreLabel(slug, 'sub') }}
              </span>
            </span>
          </div>
        </div>

        <!-- Section 2: Ratings -->
        <div class="content-section individual-tiers">
          <h3>Individual Ratings</h3>
          <div class="ratings-grid">
            <div v-for="(tier, name) in album.individual_tiers" :key="name" class="rating-item">
              <span class="reviewer-name">{{ name }}</span>
              <EnhancedTierBadge
                :tier="tier"
                variant="compact"
                size="small"
                class="rating-badge"
              />
            </div>
          </div>
        </div>

        <!-- Veto Warning (if applicable) -->
        <div v-if="veto" class="content-section veto-warning">
          <span class="veto-icon">⚠️</span>
          <span>Veto by {{ veto_by }}</span>
        </div>

        <!-- Section 3: Album Details -->
        <div v-if="loading" class="content-section loading-container">
          <LoadingSpinner size="medium" message="Loading album data..." />
        </div>
        <div v-if="error" class="content-section error-container">
          <span class="error-icon">⚠️</span>
          <p class="error-message">{{ error }}</p>
        </div>
        <div v-else-if="spotifyData" class="content-section album-metadata">
          <h3 class="section-title">
            <span class="title-icon">📀</span>
            Album Details
          </h3>
          <div class="metadata-grid">
            <div class="metadata-item">
              <span class="metadata-label">Release Date</span>
              <span class="metadata-value">{{ formatReleaseDate(spotifyData.release_date, spotifyData.release_date_precision) }}</span>
            </div>
            <div class="metadata-item" v-if="spotifyData.label">
              <span class="metadata-label">Label</span>
              <span class="metadata-value">{{ spotifyData.label }}</span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Total Tracks</span>
              <span class="metadata-value">{{ spotifyData.total_tracks || spotifyData.tracks?.length || 'N/A' }}</span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Duration</span>
              <span class="metadata-value">{{ formatAlbumDuration(spotifyData.duration_ms || calculateTotalDuration()) }}</span>
            </div>
            <div class="metadata-item" v-if="spotifyData.popularity">
              <span class="metadata-label">Popularity</span>
              <span class="metadata-value">{{ spotifyData.popularity }}/100</span>
            </div>
          </div>
        </div>

        <!-- Section 4: Track list -->
        <div v-if="spotifyData && spotifyData.tracks && spotifyData.tracks.length > 0" class="content-section tracklist-section">
          <h3 class="section-title">
            <span class="title-icon">🎵</span>
            Tracklist
          </h3>
          <div class="tracklist">
            <div
              v-for="(track, index) in spotifyData.tracks"
              :key="track.id || index"
              class="track-item"
            >
              <div class="track-number">{{ track.track_number || (index + 1) }}</div>
              <div class="track-info">
                <div class="track-name">{{ track.name }}</div>
                <div class="track-duration">{{ formatDuration(track.duration_ms) }}</div>
              </div>
            </div>
          </div>
          <div class="tracklist-summary">
            <span class="summary-text">
              {{ spotifyData.tracks.length }} tracks •
              {{ formatAlbumDuration(calculateTotalDuration()) }}
            </span>
          </div>
        </div>

        <!-- Section 5: Changelog -->
        <div class="content-section changelog-section">
          <AlbumChangelog v-if="album && album.id" :albumId="album.id" />
        </div>

        <!-- Footer: Edit and Delete Actions -->
        <div class="content-section edit-actions">
          <router-link :to="'/edit-album/' + album.id" class="edit-link-bottom">
            Edit Album
          </router-link>
          <button @click="confirmDeleteAlbum" :disabled="isDeleting" class="delete-link-bottom">
            {{ isDeleting ? 'Deleting...' : 'Delete Album' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '../../apiService';
import LoadingSpinner from '../LoadingSpinner.vue';
import EnhancedTierBadge from '../EnhancedTierBadge.vue';
import AlbumChangelog from '../changelog/AlbumChangelog.vue';
import spotifyIcon from '@/assets/icons/spotify-icon.svg';
import tidalIcon from '@/assets/icons/tidal-icon.svg';

export default {
  components: {
    LoadingSpinner,
    EnhancedTierBadge,
    AlbumChangelog
  },
  props: {
    album: Object,
    veto: Boolean,
    veto_by: String
  },
  emits: ['close', 'albumClicked', 'albumDeleted'],
  name: 'albumDetailsModal',
  data() {
    return {
      spotifyData: null,
      albumData: null, // Will hold the complete album data with session info
      loading: true,
      error: null,
      isDeleting: false,
      spotifyIcon,
      tidalIcon,
      allMainGenres: [],
      allSubGenres:  [],
    };
  },
  computed: {
  },
  async created() {
    try {
      // Fetch complete album data including session context
      this.albumData = await apiService.getAlbum(this.album.id);
    } catch (error) {
      console.error('Error fetching album data:', error);
    }

    try {
      const genres = await apiService.getGenres();
      this.allMainGenres = genres.mainGenres || [];
      this.allSubGenres  = genres.subGenres  || [];
    } catch (e) { /* non-critical */ }

    // Fetch Spotify metadata in background (non-blocking)
    if (this.album.spotify_id) {
      this.fetchSpotifyMetadata();
    } else {
      this.loading = false;
    }

  },
  methods: {
    genreLabel(slug, type = 'main') {
      const list = type === 'main' ? this.allMainGenres : this.allSubGenres;
      return list.find(g => g.slug === slug)?.label ?? slug;
    },
    async fetchSpotifyMetadata() {
      try {
        this.loading = true;
        const response = await apiService.getSpotifyAlbum(this.album.spotify_id);
        this.spotifyData = response.data;
      } catch (error) {
        console.error('Error fetching Spotify metadata:', error);
        this.error = 'Failed to fetch Spotify metadata';
      } finally {
        this.loading = false;
      }
    },
    emitAlbumClicked() {
      this.$emit('albumClicked', this.album);
    },
    formatSessionDate(dateStr) {
      if (!dateStr) return 'Unknown Date';
      const match = typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/);
      const dateOnly = match ? match[0] : dateStr;
      const d = new Date(dateOnly);
      return d.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    },
    formatDuration(durationMs) {
      if (!durationMs) return 'N/A';
      const minutes = Math.floor(durationMs / 60000);
      const seconds = ((durationMs % 60000) / 1000).toFixed(0);
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    formatAlbumDuration(durationMs) {
      if (!durationMs) return 'N/A';
      const totalMinutes = Math.floor(durationMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    },
    formatReleaseDate(releaseDate, precision) {
      if (!releaseDate) return 'N/A';
      
      if (precision === 'year') {
        return releaseDate;
      } else if (precision === 'month') {
        const [year, month] = releaseDate.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      } else {
        // Full date
        const date = new Date(releaseDate);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      }
    },
    calculateTotalDuration() {
      if (!this.spotifyData?.tracks) return 0;
      return this.spotifyData.tracks.reduce((total, track) => {
        return total + (track.duration_ms || 0);
      }, 0);
    },
    confirmDeleteAlbum() {
      const albumTitle = `${this.album.artist} - ${this.album.title}`;
      const confirmMessage = `Are you sure you want to delete "${albumTitle}"?\n\nThis action cannot be undone and will permanently remove the album and all its ratings.`;
      
      if (confirm(confirmMessage)) {
        this.deleteAlbum();
      }
    },
    async deleteAlbum() {
      if (this.isDeleting) return;
      
      this.isDeleting = true;
      
      const password = prompt('Please enter the admin password to confirm deletion:');
      if (!password) {
        this.isDeleting = false;
        return;
      }
      
      try {
        console.log('Starting delete process for album:', this.album.id);
        const result = await apiService.deleteAlbum(this.album.id, password);
        console.log('Delete result:', result);
        
        // Check if the deletion was successful
        if (result || result === null) {
          alert('Album deleted successfully!');
          this.$emit('albumDeleted', this.album.id);
          this.$emit('close');
        } else {
          console.warn('Deletion returned unexpected result:', result);
          throw new Error('Deletion completed but returned unexpected response');
        }
      } catch (error) {
        console.error('Delete album UI error:', error);
        alert(`Failed to delete album: ${error.message || 'Unknown error occurred'}`);
      } finally {
        this.isDeleting = false;
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleEscapeKey); // Add listener when the modal is mounted
    
    // On mobile, temporarily remove any body/html margins/padding that might cause white space
    if (window.innerWidth <= 600) {
      this.originalBodyStyle = {
        margin: document.body.style.margin,
        padding: document.body.style.padding,
        overflow: document.body.style.overflow
      };
      this.originalHtmlStyle = {
        margin: document.documentElement.style.margin,
        padding: document.documentElement.style.padding,
        overflow: document.documentElement.style.overflow
      };
      
      // Force remove all spacing
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
      document.documentElement.style.overflow = 'hidden';
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEscapeKey); // Remove listener when the modal is destroyed
    
    // Restore original body/html styles
    if (window.innerWidth <= 600 && this.originalBodyStyle) {
      document.body.style.margin = this.originalBodyStyle.margin;
      document.body.style.padding = this.originalBodyStyle.padding;
      document.body.style.overflow = this.originalBodyStyle.overflow;
      document.documentElement.style.margin = this.originalHtmlStyle.margin;
      document.documentElement.style.padding = this.originalHtmlStyle.padding;
      document.documentElement.style.overflow = this.originalHtmlStyle.overflow;
    }
  }
};
</script>




<style scoped>
/* Import Enhanced Tier Color System */
@import '../../styles/tier-colors.css';

/* Existing styles */
.modal-overlay {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #f3f3f3;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 32px;
  border-radius: 12px;
  font-size: 16px;
  max-height: 90vh;
  overflow-y: auto;
  width: 85%;
  max-width: 750px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  animation: simpleFadeIn 0.2s ease;
  /* Performance optimizations */
  will-change: auto;
  transform: translate3d(0, 0, 0);
  -webkit-overflow-scrolling: touch;
}

@keyframes simpleFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}


.album-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 5px;
}

.album-info img {
  width: 300px; /* Adjust this for your needs */
  height: auto;
}

.text-and-logo {
  display: flex;
  flex-direction: row; /* Desktop: left/right layout like mobile */
  justify-content: space-between;
  align-items: flex-start; /* Align to top for consistent baseline */
  width: 100%;
  padding: 16px 0;
}

.text-info {
  text-align: left; /* Desktop: left-aligned like mobile */
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0; /* Remove gap between elements */
}

.text-info p {
  font-size: 24px;
  margin: 4px 0; /* Reduce vertical margins for tighter spacing */
  color: #f3f3f3;
}

.streaming-logos {
  display: flex;
  justify-content: flex-end; /* Desktop: align right like mobile */
  align-items: flex-start; /* Align to top */
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 4px; /* Match text-info top alignment */
}

.tier-badge-header {
  margin-top: 8px;
  margin-bottom: 8px; /* Add bottom spacing */
  display: flex;
  justify-content: flex-start;
}

.streaming-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: opacity 0.3s ease;
  min-height: 24px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.streaming-logo:hover {
  opacity: 0.8;
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}

.streaming-icon {
  width: 24px;
  height: 24px;
  max-width: 24px;
  max-height: 24px;
  filter: invert(1);
  flex-shrink: 0;
}

.streaming-text {
  color: white;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* Scroll Content Container */
.scroll-content {
  background-color: rgba(30, 30, 30, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 32px;
  margin: 0 -32px -32px -32px; /* Expand to full modal width */
  width: calc(100% + 64px); /* Account for modal padding */
}

.content-section {
  margin-bottom: 24px;
}

.content-section:last-child {
  margin-bottom: 0;
}

.scroll-content p {
    margin: 0px;
  }


.modal-content .artist {
  font-weight: bold;
  color: #f3f3f3;
}

.modal-content .title {
  color: #f3f3f3;
}

.test-album-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  background: rgba(0, 188, 212, 0.9);
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid rgba(0, 188, 212, 0.5);
  box-shadow: 0 2px 4px rgba(0, 188, 212, 0.3);
  vertical-align: middle;
}

.modal-content .tier {
  font-size: 18px;
  font-weight: bold;
}

.veto-info {
  color: red;
  font-weight: bold;
  padding-top: 10px;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%);
  backdrop-filter: blur(16px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1);
  /* Ensure proper sizing */
  min-width: 44px;
  min-height: 44px;
  max-width: 44px;
  max-height: 44px;
}

.close-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
  border-color: rgba(255, 255, 255, 0.25);
  color: white;
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4), 0 4px 8px rgba(0, 0, 0, 0.2);
}

.close-btn:active {
  opacity: 0.9;
}

.veto-warning-icon{
  margin-top: 10px;
}

.tab-content h3 {
  margin-top: 15px;
  margin-bottom: 5px;
  color: #f3f3f3;
}

.modal-content .individual-tiers ul {
  list-style-type: none;
  margin: 0;
  padding-left: 0;
}

.modal-content .individual-tiers li {
  text-transform: capitalize;
}

.edit-link-inline {
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%);
  color: white;
  text-decoration: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
}

.edit-link-inline:hover {
  background: linear-gradient(135deg, rgba(37, 99, 235, 1) 0%, rgba(29, 78, 216, 1) 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  color: white;
  text-decoration: none;
}

.tab-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.tier-display {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tier-text {
  margin: 0;
  font-size: 1.5em;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.album-details {
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-row:last-child {
  border-bottom: none;
}

.label {
  font-weight: 600;
  color: #a0a0a0;
  font-size: 14px;
}

.value {
  font-weight: 500;
  color: #f3f3f3;
  text-align: right;
}

.session-context .value {
  text-align: left;
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.session-date {
  font-weight: 600;
  color: #f3f3f3;
  font-size: 14px;
}

.session-location {
  font-size: 12px;
  color: #a0a0a0;
  font-style: italic;
}


.ratings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.rating-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.reviewer-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #f3f3f3;
}

.rating-value {
  font-weight: 500;
  color: #a0a0a0;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
}

.veto-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #dc2626;
  font-weight: 500;
}

.veto-icon {
  font-size: 18px;
}

/* Edit Actions Section at Bottom */
.edit-actions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  gap: 12px;
}

.edit-link-bottom {
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%);
  color: white;
  text-decoration: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
}

.edit-link-bottom:hover {
  background: linear-gradient(135deg, rgba(37, 99, 235, 1) 0%, rgba(29, 78, 216, 1) 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  color: white;
  text-decoration: none;
  transform: translateY(-1px);
}

.delete-link-bottom {
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
}

.delete-link-bottom:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(220, 38, 38, 1) 0%, rgba(185, 28, 28, 1) 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  color: white;
  transform: translateY(-1px);
}

.delete-link-bottom:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Spotify Album Info Styles */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.error-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.15);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.error-icon {
  font-size: 20px;
}

.error-message {
  color: #f87171;
  font-weight: 500;
  margin: 0;
}

.spotify-info {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.album-metadata {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.album-metadata .section-title {
  margin-top: 0;
}

.metadata-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.metadata-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.metadata-item:last-child {
  border-bottom: none;
}

.metadata-label {
  font-weight: 600;
  color: #a0a0a0;
  font-size: 14px;
}

.metadata-value {
  font-weight: 500;
  color: #f3f3f3;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #f3f3f3;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.title-icon {
  font-size: 20px;
}

.tracklist-section {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tracklist-section .section-title {
  margin-top: 0;
}

.changelog-section {
  /* Ensure changelog section doesn't have extra spacing */
}

.tracklist {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 8px;
  /* Smooth scrolling optimizations */
  -webkit-overflow-scrolling: touch;
  will-change: scroll-position;
  transform: translateZ(0);
}

.track-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.track-item:hover {
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.track-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

.track-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.track-name {
  font-weight: 500;
  color: #f3f3f3;
  font-size: 14px;
  flex: 1;
  margin-right: 12px;
}

.track-duration {
  font-weight: 400;
  color: #a0a0a0;
  font-size: 13px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  background: rgba(255, 255, 255, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
}

.tracklist-summary {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.summary-text {
  font-size: 14px;
  color: #a0a0a0;
  font-weight: 500;
}

.additional-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.streaming-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.no-streaming-links {
  padding: 16px;
  text-align: center;
}

.muted-text {
  color: #a0a0a0;
  font-style: italic;
  margin: 0;
}

.spotify-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #1db954 0%, #1ed760 100%);
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(29, 185, 84, 0.3);
}

.spotify-button:hover {
  box-shadow: 0 6px 20px rgba(29, 185, 84, 0.4);
  color: white;
  text-decoration: none;
}

.spotify-icon {
  font-size: 16px;
}

@media (max-width: 600px) {
  /* Fix scrollbar issue causing white space on mobile */
  .modal-content::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
  }
  
  .modal-content {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  .modal-overlay {
    padding: 0 !important; /* Force remove padding for fullscreen experience */
    margin: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    align-items: stretch; /* Make modal take full height */
    justify-content: stretch; /* Make modal take full width */
    box-sizing: border-box !important;
  }
  
  .modal-content {
    /* Use viewport units and absolute positioning */
    width: 100vw !important;
    height: 100vh !important;
    max-width: none !important;
    max-height: none !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 0 !important;
    border: none !important;
    background: rgba(26, 26, 26, 0.98) !important;
    backdrop-filter: blur(16px) !important;
    -webkit-backdrop-filter: blur(16px) !important;
    overflow-y: auto;
    animation: simpleFadeIn 0.2s ease;
    /* Absolutely position relative to viewport, not parent */
    position: fixed !important;
    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 9999 !important;
    box-sizing: border-box !important;
    /* Ensure no browser defaults interfere */
    outline: none !important;
    box-shadow: none !important;
    /* Mobile performance optimizations */
    -webkit-overflow-scrolling: touch !important;
    will-change: scroll-position !important;
    transform: translateZ(0) !important;
    /* Prevent scroll interference */
    scroll-padding-bottom: 100px !important;
    overscroll-behavior: contain !important;
  }
  
  /* Force body and html to have no margins when modal is open */
  .modal-overlay {
    position: fixed !important;
    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 9998 !important;
  }

  .album-info {
    flex-direction: column;
    align-items: center;
    padding: 0px;
  }

  .text-and-logo {
    padding: 16px 20px; /* Add horizontal padding to match scroll-content */
    /* Already using row layout from desktop styles */
  }

  .streaming-logos {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .streaming-logo {
    padding: 6px 10px;
  }

  .streaming-icon {
    width: 18px;
    height: 18px;
    max-width: 18px;
    max-height: 18px;
  }
  
  .streaming-text {
    font-size: 12px;
  }

  .album-info {
    padding: 0; /* Remove any padding from album info */
  }
  
  .album-info img {
    width: 100vw; /* Full viewport width */
    height: auto; /* Keeps aspect ratio */
    object-fit: cover; /* Ensures image fits nicely */
    margin: 0; /* No margins needed since modal has no padding */
    border-radius: 0; /* Remove any border radius */
    display: block; /* Ensure no inline spacing */
  }


  .text-info {
    /* Already left-aligned from desktop styles */
    margin-bottom: 0px;
  }

  .scroll-content {
    padding: 20px; /* Add padding back since modal has none */
    padding-bottom: max(120px, calc(env(safe-area-inset-bottom) + 100px)); /* Extra padding to ensure buttons are always visible above mobile browser UI */
    margin: 0; /* No negative margins needed */
    width: 100%; /* Full width */
    scroll-padding-bottom: 100px; /* Prevent auto-scroll from hiding content */
  }

  .edit-actions {
    padding: 16px 0;
    margin-top: 20px;
  }

  .edit-link-bottom {
    padding: 14px 28px;
    font-size: 16px;
  }

  .delete-link-bottom {
    padding: 14px 28px;
    font-size: 16px;
  }

  .close-btn {
    top: max(20px, env(safe-area-inset-top)); /* Respect iOS safe area */
    right: max(20px, env(safe-area-inset-right)); /* Respect iOS safe area */
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    max-width: 40px;
    max-height: 40px;
    font-size: 16px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.98) 0%, rgba(31, 41, 55, 0.98) 100%);
    z-index: 100; /* Ensure it's above the image */
  }
  
  .modal-content {
    padding-bottom: 0; /* Remove bottom padding for edge-to-edge */
  }


}

.genres-value {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  justify-content: flex-end;
}

.genre-pill {
  padding: 0.15rem 0.5rem;
  background: rgba(167, 139, 250, 0.15);
  border: 1px solid rgba(167, 139, 250, 0.3);
  border-radius: 999px;
  color: #c4b5fd;
  font-size: 0.75rem;
}
.genre-pill--main {
  background: rgba(167, 139, 250, 0.15);
  border-color: rgba(167, 139, 250, 0.35);
  color: #ddd6fe;
}
.genre-pill--sub {
  background: rgba(94, 234, 212, 0.1);
  border-color: rgba(94, 234, 212, 0.25);
  color: #99f6e4;
  font-size: 0.75rem;
}

</style>
